/**
 * How much of the recorded time actually reached the live transcriber.
 *
 * MediaRecorder keeps the full recording even when the browser slows the page
 * (background or covered windows), but the PCM capture that feeds the live
 * transcriber can drop audio. A transcript built from that gappy audio can
 * miss the exam and plan, or the provider can fill gaps with words nobody
 * said. When too much audio is missing, the live text must not become the
 * note source; the full recording is transcribed after Stop instead.
 */

/** Always tolerated: capture start-up and the last unsent buffer. */
export const COVERAGE_MIN_MISSING_SECONDS = 4;
/** Longer visits may miss up to this share of the active recording time. */
export const COVERAGE_MAX_MISSING_FRACTION = 0.02;

export interface CaptureCoverageReport {
  /** Wall-clock recording time, excluding pauses. */
  activeSeconds: number;
  /** Audio delivered to the live transcriber during that time. */
  capturedSeconds: number;
  missingSeconds: number;
}

export const isCaptureCoverageLow = ({
  activeSeconds,
  missingSeconds,
}: CaptureCoverageReport): boolean =>
  missingSeconds >
  Math.max(
    COVERAGE_MIN_MISSING_SECONDS,
    activeSeconds * COVERAGE_MAX_MISSING_FRACTION,
  );

export class CaptureCoverage {
  private activeMs = 0;
  private activeSince: number | null = null;
  private capturedSeconds = 0;

  public constructor(
    private readonly now: () => number = () => performance.now(),
  ) {}

  /** Recording is running (idempotent). */
  public start(): void {
    if (this.activeSince === null) this.activeSince = this.now();
  }

  /** Recording is paused or stopped (idempotent). */
  public pause(): void {
    if (this.activeSince === null) return;
    this.activeMs += Math.max(0, this.now() - this.activeSince);
    this.activeSince = null;
  }

  public addSamples(samples: number, sampleRate: number): void {
    if (sampleRate > 0) this.capturedSeconds += samples / sampleRate;
  }

  public reset(): void {
    this.activeMs = 0;
    this.activeSince = null;
    this.capturedSeconds = 0;
  }

  public report(): CaptureCoverageReport {
    const running =
      this.activeSince === null
        ? 0
        : Math.max(0, this.now() - this.activeSince);
    const activeSeconds = (this.activeMs + running) / 1000;
    return {
      activeSeconds,
      capturedSeconds: this.capturedSeconds,
      missingSeconds: Math.max(0, activeSeconds - this.capturedSeconds),
    };
  }
}
